import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { USER_ROLE_ADMIN } from "@/lib/constants/roles";
import { useAssistant } from "../contexts/AssistantContext";
import { AssistantPanel } from "./AssistantPanel";

/**
 * Header launcher plus drop-down panel for the Smart Assistant.
 *
 * Rendered once by `DashboardLayout` inside the top navigation, so it is present on every admin
 * page. It hides itself for non-admins and when the backend reports the assistant as unconfigured -
 * the API enforces both anyway, this only avoids showing a control that cannot work.
 */
export const AssistantWidget: React.FC = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { isAvailable, isOpen, toggle } = useAssistant();

	const isAdmin = user?.role?.roleKey === USER_ROLE_ADMIN;
	if (!isAdmin || !isAvailable) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				onClick={toggle}
				aria-expanded={isOpen}
				aria-label={t("assistant.launcher")}
				title={t("assistant.launcher")}
				// Use the shared action radius, including for this native button.
				className={`h-12 px-4 rounded-[var(--radius-button)] flex items-center gap-2 bg-primary text-primary-foreground transition-all hover:brightness-110 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
					isOpen ? "ring-2 ring-[#1AC0E5] ring-offset-2 shadow-md" : "shadow-sm"
				}`}
			>
				<span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
					<Sparkles className="w-5 h-5" />
				</span>
				<span className="hidden sm:block text-sm font-medium leading-tight text-start whitespace-nowrap">
					{t("assistant.title")}
				</span>
			</button>
			{/* Owned here rather than by the layout, so it disappears with the button when hidden. */}
			<div className="divider h-8 w-px bg-gray-300" />
			{isOpen && <AssistantPanel />}
		</>
	);
};
